<template>
  <Flex
    center
    ref="overlay"
    class="c_overlay"
    :class="overlayClasses"
    @keydown.native.esc="hide"
    :style="overlayStyles"
    tabindex="-1"
    v-if="isActive"
  >
    <div class="c_overlay__mask c_box--mask">
      <Button class="c_overlay__close" @click="hide" v-if="showCloseButton && closeButtonOutside">
        <span class="c_overlay__close_icon">
          <IconSVG handle="close" width="15" height="15" aria-hidden="true" />
        </span>
      </Button>
      <div class="c_box--mask" @click="hide"></div>
      <div class="c_overlay__content_wrapper" :class="theme ? ['c_overlay__content_wrapper--' + theme] : ''">
        <div class="c_overlay__content" :class="contentClasses" v-if="isActive">
          <Button class="c_overlay__close" @click="hide" v-if="showCloseButton && !closeButtonOutside">
            <span class="c_overlay__close_icon">
              <IconSVG handle="close" width="15" height="15" aria-hidden="true" />
            </span>
          </Button>
          <slot></slot>
        </div>
      </div>
    </div>
  </Flex>
</template>

<script>
import Flex from 'Components/flex/Flex.vue';
import IconSVG from 'Components/icon_svg/IconSVG.vue';

import { defineComponent, computed, ref, nextTick } from '@nuxtjs/composition-api';

export default defineComponent({
  name: 'Overlay',
  components: {
    Flex,
    IconSVG,
  },
  props: {
    rounded: { type: Boolean, default: false },
    scrollable: { type: Boolean, default: false },
    showCloseButton: { type: Boolean, default: false },
    closeButtonOutside: { type: Boolean, default: false },
    theme: { type: String, default: 'default' }, // default, full, hotspot
    width: { type: String },
    height: { type: String },
  },
  setup(props, { emit }) {
    const isActive = ref(false);
    const triggerElement = ref(false);
    const overlay = ref(null);

    const contentClasses = computed(() => {
      const classes = [];
      if (props.theme === 'full') {
        classes.push('c_text--center');
      }
      if (props.scrollable) {
        classes.push('c_overlay__content--scrollable');
      }
      return classes;
    });

    const overlayClasses = computed(() => {
      return [
        `c_overlay--${props.theme}`,
        {
          'c_overlay--active': isActive.value,
          'c_overlay--rounded': props.rounded,
        },
      ];
    });
    const overlayStyles = computed(() => {
      return {
        '--overlay-max-width': props.width || false,
        '--overlay-max-height': props.height || false,
      };
    });

    const hide = () => {
      document.querySelector('html')?.classList.remove('no-scroll');
      isActive.value = false;
      emit('overlayHide');
      window.removeEventListener('keydown', handleEscapeClose);
      if (
        triggerElement.value &&
        typeof triggerElement.value.focus === 'function' &&
        document.contains(triggerElement.value)
      ) {
        triggerElement.value.focus();
      }
    };

    const show = (newState = true) => {
      document.querySelector('html')?.classList.add('no-scroll');
      isActive.value = newState;
      triggerElement.value = event.target;
      window.addEventListener('keydown', handleEscapeClose);
      nextTick(() => {
        overlay.value?.$el?.focus();
        emit('overlayShow');
      });
    };

    const handleEscapeClose = (e) => {
      if (e.key === 'Escape') {
        hide();
      }
    };

    return {
      contentClasses,
      isActive,
      triggerElement,
      overlayClasses,
      overlayStyles,
      hide,
      show,
      overlay,
      handleEscapeClose,
    };
  },
});
</script>

<style lang="scss">
.c_overlay {
  $self: &;
  margin: -50vh -50vw;
  position: fixed;
  top: 50%;
  right: 50%;
  bottom: 50%;
  left: 50%;
  width: 100vw;
  height: 100vh;
  z-index: $z_overlay;

  @at-root #{$self}__mask {
    width: 100%;
    height: 100%;
    background-color: rgba($color_black_raw, 0.9);
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  @at-root #{$self}__content_wrapper {
    position: relative;
    z-index: 2;
    max-width: 90vw;

    @at-root #{&}--video-lightbox {
      width: 900px;
    }

    @at-root #{$self}--default & {
      display: flex;
      flex-flow: column nowrap;
      align-items: center;
      justify-content: center;
    }
    @at-root #{$self}--hotspot & {
      width: 100%;
    }

    @at-root #{$self}__close_icon {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      background-color: #f6f6f6;
      display: flex;
      align-items: center;
      justify-content: center;
      color: black;

      &:hover {
        background-color: #dddddd;
        color: #333;
      }
    }
  }
  @at-root #{$self}__content {
    box-sizing: border-box;
    display: flex;
    flex-flow: column nowrap;
    align-items: center;
    text-align: center;
    .c_caption {
      flex: 0 1 100%;
    }

    .c_video {
      width: 100%;
      aspect-ratio: 16 / 9;
      height: auto;
      max-width: 100%;
    }

    @at-root #{$self}--default & {
      justify-content: center;
      height: 70%;
      min-height: 100%;
    }
    @at-root #{$self}--image & {
      justify-content: center;
      width: var(--overlay-max-width);
      height: var(--overlay-max-height);
    }
    @at-root #{$self}--rounded & > * {
      border-radius: 8px;
    }
    @at-root #{&}--scrollable {
      max-height: calc(100vh - 40px);
    }
  }
  #{$self}__close {
    position: fixed;
    z-index: 3;
    align-self: flex-end;
    top: px_to_rem(20);
    right: px_to_rem(20);
    background: none;
    border: none;
    color: #91969b;

    &:hover {
      color: white;
      cursor: pointer;
    }
  }
}
</style>
